import { InputAdornment, TextField, Tooltip } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { fieldId } from './constants'
import { calcExample, formatDecimal, formatMoney, formatPercent, getIn } from './utils'

const GRID =
  'grid grid-cols-2 gap-x-4 gap-y-3 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.85fr)_minmax(0,1.15fr)] md:gap-x-5 md:items-start'

const MUTED = 'text-gray-500 dark:text-gray-400'

const CELL_LABEL = `md:hidden block text-xs uppercase font-medium mb-1 ${MUTED}`

export const currencySymbol = currency =>
  currency === 'UYU' ? '$' : currency === 'USD' ? 'US$' : 'ARS'

export const CurrencyChip = ({ currency }) => (
  <span className='text-xs font-medium text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-dk-raised px-2 py-0.5 rounded'>
    {currency}
  </span>
)

const HeaderHint = ({ label, hint }) => (
  <Tooltip title={hint} arrow placement='top'>
    <span className='inline-flex items-center gap-1.5 cursor-help w-fit'>
      {label}
      <FontAwesomeIcon icon={faCircleInfo} className='text-[11px] opacity-70' />
    </span>
  </Tooltip>
)

export default function RateTable({ groups, draft, saved, fieldStates, canEdit, onChange }) {
  return (
    <div className='bg-white dark:bg-dk-surface rounded-xl shadow-sm' role='table' aria-label='Tarifas por ruta'>
      <div
        role='row'
        className={`${GRID} hidden md:grid sticky top-0 z-10 rounded-t-xl px-5 py-2.5 bg-gray-50 dark:bg-dk-raised border-b border-gray-100 dark:border-dk-border text-xs uppercase font-medium ${MUTED}`}
      >
        <span role='columnheader'>Ruta</span>
        <span role='columnheader'>
          <HeaderHint
            label='Precio por km'
            hint='Precio base por kilómetro. En viajes cortos se suma un recargo que baja a medida que crece la distancia.'
          />
        </span>
        <span role='columnheader'>
          <HeaderHint
            label='Multiplicador máx.'
            hint='Precio máximo permitido = precio sugerido × multiplicador.'
          />
        </span>
        <span role='columnheader'>
          <HeaderHint
            label='Precio sugerido'
            hint='Ejemplo con la distancia de referencia de cada ruta, redondeado a ARS 250, $ 5 o US$ 1 según la moneda.'
          />
        </span>
      </div>

      {groups.map((group, index) => (
        <div role='rowgroup' key={group.currency}>
          <div
            className={`flex items-center gap-2 px-5 py-2 bg-gray-50/60 dark:bg-dk-raised/40 border-b border-gray-100 dark:border-dk-border ${
              index > 0 ? 'border-t' : 'rounded-t-xl md:rounded-none'
            }`}
          >
            <span className='text-sm font-semibold text-gray-800 dark:text-gray-100'>{group.label}</span>
            <CurrencyChip currency={group.currency} />
            <span className={`ml-auto text-xs ${MUTED}`}>
              {group.rows.length} {group.rows.length === 1 ? 'tarifa' : 'tarifas'}
            </span>
          </div>
          {group.rows.map(row => (
            <RateRow
              key={row.id}
              row={row}
              group={group}
              draft={draft}
              saved={saved}
              fieldStates={fieldStates}
              canEdit={canEdit}
              onChange={onChange}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

const RateRow = ({ row, group, draft, saved, fieldStates, canEdit, onChange }) => {
  const priceId = fieldId(row.price)
  const multiplierId = fieldId(row.multiplier)
  const priceState = fieldStates[priceId]
  const multiplierState = fieldStates[multiplierId]
  const rowChanged = priceState.changed || (!row.sharesMultiplier && multiplierState.changed)

  const example = calcExample(priceState.value, row.exampleKm, group.round, multiplierState.value)
  const previousExample = calcExample(
    getIn(saved, row.price),
    row.exampleKm,
    group.round,
    getIn(saved, row.multiplier)
  )
  const exampleChanged =
    example &&
    previousExample &&
    (example.price !== previousExample.price || example.maxPrice !== previousExample.maxPrice)

  return (
    <div
      role='row'
      className={`${GRID} relative px-5 py-4 border-b last:border-b-0 border-gray-100 dark:border-dk-border transition-colors duration-150 ${
        rowChanged ? 'bg-primary/[0.04] dark:bg-primary/[0.08]' : 'hover:bg-gray-50/70 dark:hover:bg-dk-hover/40'
      }`}
    >
      {rowChanged && <span aria-hidden className='absolute left-0 inset-y-0 w-[3px] bg-primary' />}

      <div role='cell' className='col-span-2 md:col-span-1 min-w-0 md:pt-2'>
        <p className='flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100'>
          {row.label}
          {rowChanged && (
            <span className='text-[11px] font-medium text-primary dark:text-[#e0715e] bg-primary/10 dark:bg-primary/20 px-1.5 py-0.5 rounded'>
              Modificado
            </span>
          )}
        </p>
        {row.subtitle && <p className={`text-xs mt-0.5 ${MUTED}`}>{row.subtitle}</p>}
      </div>

      <ValueCell
        id={priceId}
        kind='price'
        label='Precio por km'
        ariaLabel={`${group.label} ${row.label}: precio por km`}
        currency={group.currency}
        text={draft[priceId]}
        state={priceState}
        canEdit={canEdit}
        onChange={onChange}
      />

      {row.sharesMultiplier ? (
        <div role='cell' className='md:pt-2'>
          <span className={CELL_LABEL}>Multiplicador máx.</span>
          <p className='text-sm text-gray-700 dark:text-gray-300 tabular-nums'>
            {formatDecimal(multiplierState.value) || '—'} ×
          </p>
          <p className={`text-xs mt-0.5 ${MUTED}`}>Igual que el tramo corto</p>
        </div>
      ) : (
        <ValueCell
          id={multiplierId}
          kind='multiplier'
          label='Multiplicador máx.'
          ariaLabel={`${group.label} ${row.label}: multiplicador máximo`}
          currency={group.currency}
          text={draft[multiplierId]}
          state={multiplierState}
          canEdit={canEdit}
          onChange={onChange}
        />
      )}

      <div role='cell' className='col-span-2 md:col-span-1 md:pt-2'>
        <span className={CELL_LABEL}>Precio sugerido</span>
        {example ? (
          <>
            <p className='text-sm font-semibold text-gray-800 dark:text-gray-100 tabular-nums'>
              {formatMoney(example.price, group.currency)}
            </p>
            <p className={`text-xs mt-0.5 tabular-nums ${MUTED}`}>
              {row.exampleKm} km · máx. {formatMoney(example.maxPrice, group.currency)}
            </p>
            {exampleChanged && (
              <p className={`text-xs mt-0.5 tabular-nums ${MUTED}`}>
                Antes {formatMoney(previousExample.price, group.currency)} · máx.{' '}
                {formatMoney(previousExample.maxPrice, group.currency)}
              </p>
            )}
          </>
        ) : (
          <p className={`text-xs ${MUTED}`}>
            {row.exampleKm ? '—' : 'Sin distancia de referencia'}
          </p>
        )}
      </div>
    </div>
  )
}

const changedOutline = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' }
}

const ValueCell = ({ id, kind, label, ariaLabel, currency, text, state, canEdit, onChange }) => {
  const helperId = `${id}-helper`
  const showHelper = state.changed

  return (
    <div role='cell' className='min-w-0'>
      <span className={CELL_LABEL}>{label}</span>
      {canEdit ? (
        <TextField
          size='small'
          fullWidth
          hiddenLabel
          value={text}
          placeholder='Sin configurar'
          error={Boolean(state.error)}
          onChange={e => onChange(id, e.target.value)}
          sx={state.changed && !state.error ? changedOutline : undefined}
          inputProps={{
            inputMode: 'decimal',
            autoComplete: 'off',
            'aria-label': ariaLabel,
            'aria-invalid': Boolean(state.error),
            'aria-describedby': showHelper ? helperId : undefined,
            className: 'tabular-nums'
          }}
          InputProps={
            kind === 'price'
              ? {
                  startAdornment: (
                    <InputAdornment position='start'>{currencySymbol(currency)}</InputAdornment>
                  )
                }
              : { endAdornment: <InputAdornment position='end'>×</InputAdornment> }
          }
        />
      ) : (
        <p className='text-sm font-medium text-gray-800 dark:text-gray-100 tabular-nums md:pt-2'>
          {kind === 'price'
            ? `${currencySymbol(currency)} ${formatDecimal(state.previous) || '—'}`
            : `${formatDecimal(state.previous) || '—'} ×`}
        </p>
      )}
      {showHelper && <FieldHelper id={helperId} state={state} />}
    </div>
  )
}

const FieldHelper = ({ id, state }) => {
  if (state.error) {
    return (
      <p id={id} role='alert' className='text-xs mt-1 text-red-600 dark:text-red-400'>
        {state.error}
      </p>
    )
  }

  return (
    <p id={id} className={`text-xs mt-1 tabular-nums ${MUTED}`}>
      Antes {formatDecimal(state.previous) || '—'}
      {state.ratio != null && (
        <>
          {' · '}
          <span
            className={
              state.bigChange ? 'font-medium text-amber-700 dark:text-amber-400' : undefined
            }
          >
            {state.bigChange && (
              <FontAwesomeIcon icon={faTriangleExclamation} className='mr-1' aria-hidden />
            )}
            {formatPercent(state.ratio)}
          </span>
        </>
      )}
    </p>
  )
}
